
  import {
    defineComponent,
    onMounted,
    ref,
    computed,
    onBeforeUnmount,
  } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';

  interface GiftUserObject {
    id: number;
    fullName: string;
    phone: string;
    email: string;
    userType: string;
    memberType: number;
  }

  interface GiftPackageObject {
    id: number;
    titleEn: string;
    titleAr: string;
    daysCovered: number;
    price: number;
    iveCredits: number;
    adsCredits: number;
    specialAds: number;
    type: number;
    slug: string;
  }

  export default defineComponent({
    name: 'subscriptions-creating',
    components: {
      ErrorMessage,
      Field,
      Form,
    },
    setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const { can } = useAbility();

      const searchPhone = ref('');
      const searchEmail = ref('');
      const searchResults = ref<GiftUserObject[]>([]);
      const selectedUser = ref<GiftUserObject | null>(null);
      const isSearching = ref(false);
      const searchPerformed = ref(false);
      const searchTimeout = ref<number | null>(null);

      const selectedPackageId = ref('');
      const giftPackages = ref<GiftPackageObject[]>([]);

      const translate = (text: string) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      const validationSchema = Yup.object().shape({
        packageId: Yup.string()
          .required(() => translate('PACKAGE_IS_REQUIRED_FIELD'))
          .label('Package'),
        note: Yup.string()
          .max(500, () => translate('NOTE_MAX_500_CHARS'))
          .label('Note'),
      });

      const onSearchInput = () => {
        if (searchTimeout.value) {
          clearTimeout(searchTimeout.value);
        }

        const phone = searchPhone.value.trim();
        const email = searchEmail.value.trim();

        if (!phone && !email) {
          searchResults.value = [];
          searchPerformed.value = false;
          return;
        }

        searchTimeout.value = window.setTimeout(async () => {
          await performSearch();
        }, 300);
      };

      const performSearch = async () => {
        const phone = searchPhone.value.trim();
        const email = searchEmail.value.trim();

        if (!phone && !email) {
          return;
        }

        isSearching.value = true;
        searchPerformed.value = true;

        try {
          const params: { phone?: string; email?: string } = {};
          if (phone) params.phone = phone;
          if (email) params.email = email;

          const result = await store.dispatch(
            Actions.SEARCH_GIFT_USERS,
            params
          );
          if (result && result.data) {
            searchResults.value = result.data;
          }
        } catch (error) {
          console.error('Search error:', error);
          searchResults.value = [];
        } finally {
          isSearching.value = false;
        }
      };

      const selectUser = (user: GiftUserObject) => {
        selectedUser.value = user;
        searchResults.value = [];
        searchPhone.value = '';
        searchEmail.value = '';
        searchPerformed.value = false;
      };

      const clearSelectedUser = () => {
        selectedUser.value = null;
      };

      const selectedPackage = computed(() => {
        if (!selectedPackageId.value) return null;
        return giftPackages.value.find(
          (pkg) => pkg.id === Number(selectedPackageId.value)
        );
      });

      const getPackageTypeLabel = (type: number) => {
        switch (type) {
          case 1:
            return translate('mixPackage');
          case 2:
            return translate('subscriptionOnly');
          case 3:
            return translate('creditsOnly');
          default:
            return translate('unknown');
        }
      };

      const getPackageTypeBadge = (type: number) => {
        switch (type) {
          case 1:
            return 'badge-light-primary';
          case 2:
            return 'badge-light-success';
          case 3:
            return 'badge-light-warning';
          default:
            return 'badge-light-secondary';
        }
      };

      const loadGiftPackages = async () => {
        try {
          const result = await store.dispatch(Actions.GET_GIFT_PACKAGES);
          if (result && result.data) {
            giftPackages.value = result.data;
          }
        } catch (error) {
          console.error('Error loading packages:', error);
        }
      };

      const onSubmitGift = async (values) => {
        if (!selectedUser.value) {
          Swal.fire({
            text: translate('PLEASE_SELECT_USER'),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          return;
        }

        if (submitButton.value) {
          submitButton.value.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        const payload = {
          userId: selectedUser.value.id,
          packageId: Number(values.packageId),
          note: values.note || undefined,
        };

        try {
          await store.dispatch(Actions.ASSIGN_GIFT_PACKAGE, payload);

          Swal.fire({
            text: translate('SUCCESSFULLY_GIFTED_PACKAGE'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.push({ name: 'purchase-logs-listing' });
          });
        } catch (err: any) {
          const errors = store.getters.getErrors;
          const firstErrorKey = Object.keys(errors ?? {})[0];
          const message =
            firstErrorKey && errors[firstErrorKey]?.[0]
              ? translate(errors[firstErrorKey][0])
              : translate('GIFT_PACKAGE_FAILED');

          Swal.fire({
            text: message,
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
        } finally {
          submitButton.value?.removeAttribute('data-kt-indicator');
          submitButton.value!.disabled = false;
        }
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('giftSubscription'), [
          translate('subscriptions'),
        ]);
        loadGiftPackages();
      });

      onBeforeUnmount(() => {
        store.dispatch(Actions.RESET_GIFT_USERS_STORE);
        store.dispatch(Actions.RESET_GIFT_PACKAGES_STORE);
        if (searchTimeout.value) {
          clearTimeout(searchTimeout.value);
        }
      });

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        onSubmitGift,
        submitButton,
        translate,
        validationSchema,
        goBack,
        can,
        searchPhone,
        searchEmail,
        searchResults,
        selectedUser,
        isSearching,
        searchPerformed,
        onSearchInput,
        selectUser,
        clearSelectedUser,
        giftPackages,
        selectedPackageId,
        selectedPackage,
        getPackageTypeLabel,
        getPackageTypeBadge,
      };
    },
  });
